import * as React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import SpeciesActions from './Actions'

const SpecieDetailCell = ({ row, familyOptions, onUpdateFamily, familyUpdateState }) => {
  const [selectedFamilyId, setSelectedFamilyId] = React.useState(
    row?.idFamilia !== undefined && row?.idFamilia !== null ? String(row.idFamilia) : ''
  )

  React.useEffect(() => {
    setSelectedFamilyId(row?.idFamilia !== undefined && row?.idFamilia !== null ? String(row.idFamilia) : '')
  }, [row.__parentId, row.idFamilia])

  const currentFamilyId = row?.idFamilia !== undefined && row?.idFamilia !== null ? String(row.idFamilia) : ''
  const hasCurrentOption = familyOptions.some(option => String(option.id) === currentFamilyId)
  const selectableFamilyOptions = !currentFamilyId || hasCurrentOption
    ? familyOptions
    : [{ id: row.idFamilia, nombre: `${row.familiaNombre || 'Familia actual'} (actual)` }, ...familyOptions]
  const familyStatus = row.familiaActiva ? 'Activa' : 'Sin familia activa / deshabilitada'
  const isUpdating = familyUpdateState?.status === 'loading'
  const updateError = familyUpdateState?.status === 'error' ? familyUpdateState?.message : null
  const updateSuccess = familyUpdateState?.status === 'success'
  const canUpdate = Boolean(selectedFamilyId) && selectedFamilyId !== currentFamilyId

  return (
    <Box sx={{ width: '100%', py: 1.5, pr: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 1.2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Detalle de especie
        </Typography>
        <Chip
          label={`Estado familia: ${familyStatus}`}
          size="small"
          color={row.familiaActiva ? 'success' : 'default'}
          variant="outlined"
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1.2fr' },
          gap: 1.5,
          alignItems: 'start',
        }}>
        <Box
          sx={{
            p: 1.2,
            borderRadius: 1,
            border: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: '#fff',
          }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
            <Chip label={`ID especie: ${row.__parentId}`} size="small" color="primary" variant="outlined" />
            <Chip
              label={`ID familia: ${row.idFamilia ?? 'No disponible'}`}
              size="small"
              color="default"
              variant="outlined"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Familia actual: {row.familiaNombre || 'No disponible'}
          </Typography>
        </Box>

        <Box
          sx={{
            p: 1.2,
            borderRadius: 1,
            border: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: '#fff',
          }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: 0.8 }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 } }}>
              <InputLabel id={`specie-family-label-${row.__parentId}`}>Cambiar familia</InputLabel>
              <Select
                labelId={`specie-family-label-${row.__parentId}`}
                value={selectedFamilyId}
                label="Cambiar familia"
                onChange={event => setSelectedFamilyId(event.target.value)}>
                {selectableFamilyOptions.map(option => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    {option.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              disabled={!canUpdate || isUpdating}
              onClick={() => onUpdateFamily(row.__parentId, Number(selectedFamilyId))}>
              {isUpdating ? 'Actualizando...' : 'Actualizar familia'}
            </Button>
          </Stack>

          {updateSuccess && (
            <Typography variant="caption" color="success.main">
              Familia actualizada correctamente.
            </Typography>
          )}

          {updateError && (
            <Alert severity="error" sx={{ py: 0, mt: 0.8 }}>
              {updateError}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export const getColumns = (
  rowId,
  setRowId,
  expandedRowIds,
  onToggleRow,
  familyOptions,
  onUpdateFamily,
  familyUpdateBySpecieId,
  onRequestDelete,
) => {
  const columns = [
    {
      field: '__expand',
      headerName: '',
      headerAlign: 'center',
      align: 'center',
      width: 54,
      minWidth: 54,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      cellClassName: params => (params.row?.__isDetail ? 'specie-detail-cell' : 'specie-expand-cell'),
      renderCell: params => {
        if (params.row?.__isDetail) {
          const parentId = params.row.__parentId
          return (
            <SpecieDetailCell
              row={params.row}
              familyOptions={familyOptions}
              onUpdateFamily={onUpdateFamily}
              familyUpdateState={familyUpdateBySpecieId[parentId]}
            />
          )
        }

        const isExpanded = expandedRowIds.includes(params.row.id)
        return (
          <IconButton
            size="small"
            aria-label={isExpanded ? 'Contraer detalle' : 'Expandir detalle'}
            onClick={event => {
              event.stopPropagation()
              onToggleRow(params.row.id)
            }}>
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        )
      },
    },
    {
      field: 'nombre_cientifico',
      headerName: 'Nombre científico',
      headerAlign: 'center',
      flex: 2,
      minWidth: 170,
      editable: true,
    },
    {
      field: 'clasificador',
      headerName: 'Clasificador',
      headerAlign: 'center',
      flex: 1.6,
      minWidth: 120,
      editable: true,
    },
    {
      field: 'habilitado',
      headerAlign: 'center',
      headerName: 'Habilitado',
      type: 'boolean',
      flex: 1,
      minWidth: 120,
      editable: true,
    },
    {
      field: 'estado_validacion',
      headerName: 'Estado verificado',
      headerAlign: 'center',
      align: 'center',
      type: 'singleSelect',
      editable: true,
      valueOptions: [
        { value: 'pendiente', label: 'Pendiente' },
        { value: 'verificado', label: 'Verificado' },
      ],
      flex: 1.6,
      minWidth: 170,
      valueFormatter: params => {
        if (params?.value === 'verificado') return 'Verificado'
        if (params?.value === 'pendiente') return 'Pendiente'
        return 'No definido'
      },
    },
    {
      field: 'actions',
      headerAlign: 'center',
      headerName: 'Acciones',
      type: 'actions',
      renderCell: params =>
        params.row?.__isDetail ? null : <SpeciesActions {...{ params, rowId, setRowId, onRequestDelete }} />,
      minWidth: 130,
      flex: 1,
    },
  ]

  const totalColumns = columns.length
  columns[0].colSpan = params => (params.row?.__isDetail ? totalColumns : 1)

  return columns
}
